import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../common/state/store";
import { createNewScene } from "../../projects/state/projectsSlice";

export interface IFlowViewport {
  projectId: number;
  x: number;
  y: number;
  zoom: number;
}

export interface IFlowChainsState {
  projectId: number;
  collapsedKeys: string[];
  foldAllMode: boolean;
}

interface IGraphViewState {
  projectId: number | null;
  scale: number;
  translateX: number;
  translateY: number;
  flowViewport: IFlowViewport | null;
  flowChains: IFlowChainsState | null;
  interiorSceneStack: number[];
  latestCreatedSceneContentId: number | null;
}

const initialState: IGraphViewState = {
  projectId: null,
  scale: 1,
  translateX: 0,
  translateY: 0,
  flowViewport: null,
  flowChains: null,
  interiorSceneStack: [],
  latestCreatedSceneContentId: null,
};

export const graphViewSlice = createSlice({
  name: "graphView",
  initialState,
  reducers: {
    setGraphView: (
      state,
      action: PayloadAction<
        Omit<
          IGraphViewState,
          "flowViewport" | "flowChains" | "interiorSceneStack" | "latestCreatedSceneContentId"
        >
      >,
    ) => {
      state.projectId = action.payload.projectId;
      state.scale = action.payload.scale;
      state.translateX = action.payload.translateX;
      state.translateY = action.payload.translateY;
    },
    setFlowGraphView: (state, action: PayloadAction<IFlowViewport>) => {
      state.flowViewport = action.payload;
    },
    setFlowChains: (state, action: PayloadAction<IFlowChainsState>) => {
      state.flowChains = action.payload;
    },
    setInteriorSceneStack: (state, action: PayloadAction<number[]>) => {
      state.interiorSceneStack = action.payload;
    },
    setLatestCreatedSceneContentId: (state, action: PayloadAction<number | null>) => {
      state.latestCreatedSceneContentId = action.payload;
    },
    resetGraphView: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(createNewScene.fulfilled, (state, action) => {
      const contentId = Number(action.payload.data.sceneContent?.id);
      state.latestCreatedSceneContentId = contentId > 0 ? contentId : null;
    });
  },
});

export const {
  setGraphView,
  setFlowGraphView,
  setFlowChains,
  setInteriorSceneStack,
  setLatestCreatedSceneContentId,
  resetGraphView,
} = graphViewSlice.actions;

export const getGraphView = (state: RootState) => state.graphView;
export const getFlowGraphView = (state: RootState) => state.graphView.flowViewport;
export const getFlowChains = (state: RootState) => state.graphView.flowChains;
export const getInteriorSceneStack = (state: RootState) => state.graphView.interiorSceneStack;
export const getLatestCreatedSceneContentId = (state: RootState) =>
  state.graphView.latestCreatedSceneContentId;

export default graphViewSlice.reducer;
